import { useNavigate, useLocation } from "react-router-dom";
import { Home, Plus, User, Bell } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import useNavigationNotifications from "@/hooks/useNavigationNotifications";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuthSession } from "@/contexts/AuthSessionContext";

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}

export const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isAuthed, isLoadingAuth } = useAuthSession();
  const { notifications } = useNavigationNotifications();
  const { unreadCount } = useUnreadNotifications();

  if (!isMobile) return null;
  // The bottom nav points exclusively at authenticated destinations, so it must
  // never render for signed-out visitors on public/marketing routes.
  if (isLoadingAuth || !isAuthed) return null;
  if (location.pathname === "/" || location.pathname === "/auth") return null;

  const navItems: NavItem[] = [
    { path: "/dashboard", icon: <Home className="w-5 h-5" />, label: "Home", badge: notifications.dashboard > 0 ? notifications.dashboard : undefined },
    { path: "/create", icon: <Plus className="w-5 h-5" />, label: "Create" },
    { path: "/notifications", icon: <Bell className="w-5 h-5" />, label: "Alerts", badge: unreadCount > 0 ? unreadCount : undefined },
    { path: "/profile", icon: <User className="w-5 h-5" />, label: "Profile", badge: notifications.profile > 0 ? notifications.profile : undefined },
  ];

  return (
    <motion.nav
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-[60] bg-card border-t-[3px] border-sticker-ink shadow-[0_-3px_0_hsl(var(--sticker-ink))]"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
    >
      <div className="grid grid-cols-4 items-stretch h-[68px] max-w-lg mx-auto px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                "relative flex flex-col items-center justify-center min-w-0 min-h-[56px] h-full rounded-2xl transition-all duration-200 active:scale-[0.96]",
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute top-1 left-1/2 -translate-x-1/2 w-7 h-[3px] bg-primary rounded-full"
                  />
                )}
              </AnimatePresence>
              <div className="relative">
                <motion.div whileTap={{ scale: 0.9 }} className={cn("p-1.5 rounded-xl transition-all duration-200", isActive && "bg-primary/10")}>
                  {item.icon}
                </motion.div>
                {item.badge && item.badge > 0 && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1">
                    {item.badge > 99 ? "99+" : item.badge}
                  </motion.span>
                )}
              </div>
              <span className={cn("text-[11px] mt-0.5 font-bold transition-all", isActive && "font-extrabold")}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </motion.nav>
  );
};

export default BottomNavBar;
